import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft, ArrowDownWideNarrow, ArrowUpWideNarrow, Gift } from "lucide-react";
import useRewardsData from "../hooks/useRewardsData";
import {
  formatVoucherExpiry,
  formatVoucherReceivedDate,
  getVoucherExpiryDate,
  getVoucherReceivedDate,
  isVoucherExpired,
} from "../utils/rewardFormatters";
import { getListDateLocale } from "../utils/dateLocale";

export default function MyRewardsPage({
  handleBackToMain,
  userRewards,
  isLoadingRewards,
  rewardsLoadError,
  loadUserRewards,
  handleVoucherClick,
}) {
  const { t } = useTranslation();
  const [activeRewardTab, setActiveRewardTab] = useState("token");
  const [sortOrder, setSortOrder] = useState("newest");
  const { sortedRewards } = useRewardsData({
    rewards: userRewards,
    activeRewardTab,
    sortOrder,
  });
  const sortLabel = t(sortOrder === "newest" ? "rewards.sortNewest" : "rewards.sortOldest");
  const asset = (name) => `${process.env.PUBLIC_URL}/images/game/${name}.webp`;
  return (
    <div className="shake-list-page">
      <div className="shake-list-heading">
        <button
          className="shake-icon-button"
          aria-label={t("common.backToMain")}
          onClick={handleBackToMain}
        >
          <ArrowLeft size={22} />
        </button>
        <h1>{t("main.myRewards")}</h1>
      </div>
      <div className="shake-tabs" role="tablist">
        <button
          role="tab"
          aria-selected={activeRewardTab === "token"}
          className={`tab-btn ${activeRewardTab === "token" ? "active" : ""}`}
          onClick={() => setActiveRewardTab("token")}
        >
          {t("common.token")}
        </button>
        <button
          role="tab"
          aria-selected={activeRewardTab === "voucher"}
          className={`tab-btn ${activeRewardTab === "voucher" ? "active" : ""}`}
          onClick={() => setActiveRewardTab("voucher")}
        >
          {t("common.voucher")}
        </button>
      </div>
      <div className="shake-sort-bar">
        <button
          type="button"
          className="shake-sort-toggle"
          aria-label={`${t("rewards.sortBy")}: ${sortLabel}`}
          onClick={() =>
            setSortOrder((order) => order === "newest" ? "oldest" : "newest")
          }
        >
          {sortOrder === "newest" ? (
            <ArrowDownWideNarrow size={18} aria-hidden="true" />
          ) : (
            <ArrowUpWideNarrow size={18} aria-hidden="true" />
          )}
        </button>
      </div>
      <div className="shake-list">
        {isLoadingRewards ? (
          <div
            className="shake-list-skeleton"
            role="status"
            aria-label={t("rewards.loading")}
          >
            <div className="ui-skeleton" />
            <div className="ui-skeleton" />
            <div className="ui-skeleton" />
          </div>
        ) : rewardsLoadError ? (
          <div className="shake-empty" role="alert">
            <p>{t("rewards.loadFailed")}</p>
            <button
              className="join-shake-btn shake-secondary-button"
              onClick={loadUserRewards}
            >
              {t("common.retry")}
            </button>
          </div>
        ) : sortedRewards.length ? (
          sortedRewards.map((item) => {
            const voucher = item.type === "voucher",
              expired = voucher && isVoucherExpired(item),
              expiry = voucher
                ? formatVoucherExpiry(getVoucherExpiryDate(item), t)
                : null;
            const received = voucher
              ? formatVoucherReceivedDate(getVoucherReceivedDate(item))
              : null;
            const date = item.createdAt
              ? new Date(item.createdAt).toLocaleDateString(
                  getListDateLocale(),
                  {
                    day: "2-digit",
                    month: "short",
                    hour: "2-digit",
                    minute: "2-digit",
                  },
                )
              : "";
            const img =
              item.image?.imageUrl || asset(voucher ? "voucher" : "coin");
            return (
              <div
                key={item.id}
                className={`shake-list-row ${voucher ? "voucher-item" : "reward-item"} ${expired ? "expired" : ""}`}
                onClick={voucher ? () => handleVoucherClick(item) : undefined}
                onKeyDown={
                  voucher
                    ? (e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleVoucherClick(item);
                        }
                      }
                    : undefined
                }
                role={voucher ? "button" : undefined}
                tabIndex={voucher ? 0 : undefined}
              >
                <div className="shake-reward-icon">
                  <img
                    src={img}
                    alt=""
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = asset(voucher ? "voucher" : "coin");
                    }}
                  />
                </div>
                <div className="shake-row-content">
                  <strong>
                    {voucher
                      ? item.source ||
                        t("rewards.percentOff", { amount: item.amount })
                      : t("rewards.tokenTitle", {
                          name: item.source || item.tokenType,
                        })}
                  </strong>
                  <span>
                    {voucher
                      ? received
                        ? t("rewards.receivedOn", { date: received })
                        : t("rewards.receivedUnavailable")
                      : date}
                  </span>
                  {voucher && <span>{expiry.text}</span>}
                </div>
                <div className="shake-row-end">
                  {!voucher && <b>+{item.amount}</b>}
                  {item.deliveryStatus === "processing" ? (
                    <span className="shake-status">
                      {t("rewards.processingTag")}
                    </span>
                  ) : voucher ? (
                    <span className="shake-reward-action">
                      {expired ? t("rewards.expiredTag") : t("rewards.useNow")}
                    </span>
                  ) : (
                    <span>{t("rewards.fromApp")}</span>
                  )}
                </div>
              </div>
            );
          })
        ) : (
          <div className="shake-empty">
            <div className="shake-modal-icon">
              <Gift size={28} />
            </div>
            <p>{t("rewards.empty")}</p>
            <button
              className="join-shake-btn shake-secondary-button"
              onClick={handleBackToMain}
            >
              {t("rewards.join")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
